import Cookies from "js-cookie";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  COGNITO_ID_TOKEN_KEY,
  ITAS_AR_PROCLIE_KEY,
  ITAS_AR_TOKEN_KEY,
} from "src/config";
import { Paths } from "src/navigation";
import { GET_TOKEN_HEADER, useGetIdToken, usePostDraft } from "src/networking";
import { useCurrentStepStore } from "src/storage";
import {
  useDraftCAIStore,
  useDraftIDStore,
} from "src/storage/formik/formik-draft.store";
import {
  BozzaStepDTO,
  FormikStep,
  InfoRequestDTO,
  InfoResponseDTO,
  Optional,
} from "src/types";
import { useBoolean } from "src/utils";

export const useManagerRoot = () => {
  const navigate = useNavigate();

  //Draft id
  const currentDraftId = useDraftIDStore((state) => state.draftID);
  const setDraftId = useDraftIDStore((state) => state.setDraftID);
  const resetDraftId = useDraftIDStore((state) => state.resetDraftID);

  //Draft
  const { crossGate: postDraft } = usePostDraft();
  const [draftStatus, setDraftStatus] =
    useState<Optional<BozzaStepDTO>>(undefined);
  const resetDraft = useDraftCAIStore((state) => state.resetDraft);

  //Formik step
  const setCurrentStep = useCurrentStepStore((state) => state.setCurrentStep);
  const resetCurrentStep = useCurrentStepStore(
    (state) => state.resetCurrentStep,
  );

  //AR login status
  const proclie = Cookies.get(ITAS_AR_PROCLIE_KEY);
  const arToken = Cookies.get(ITAS_AR_TOKEN_KEY);
  const isLogged = !!arToken && !!proclie;

  //Vehicle A Plate
  const [modalPlateVisibility, showModalPlate, hideModalPlate] =
    useBoolean(false);
  const [searchParams] = useSearchParams();
  const vehicleAPlate = useRef<string | null>(searchParams.get("targa"));
  const setVehicleAPlateStore = useDraftCAIStore(
    (state) => state.setVehcileAPlate,
  );

  //Cognito token
  const { crossGate: getIdToken } = useGetIdToken();
  const requestToken = async () => {
    await getIdToken(
      {
        headers: GET_TOKEN_HEADER,
      },
      {
        onSuccess: (response) => {
          const idToken = response?.idToken;
          if (idToken) {
            Cookies.set(COGNITO_ID_TOKEN_KEY, `Bearer ${idToken}`);
          }
        },
      },
    );
  };

  //Post Draft request
  const postDraftRequestLogged = async ({ proclie }: InfoRequestDTO) => {
    await postDraft(
      {
        body: { proclie },
      },
      {
        onSuccess: (response) => managePostDraftRequest(response),
      },
    );
  };

  const postDraftRequestNotLogged = async ({
    targa,
    dataNascita,
  }: InfoRequestDTO) => {
    hideModalPlate();
    await postDraft(
      {
        body: { targa, dataNascita },
      },
      {
        onSuccess: (response) => managePostDraftRequest(response),
      },
    );
  };

  const managePostDraftRequest = (response: InfoResponseDTO) => {
    const draftId = response.idBozza;

    if (draftId != currentDraftId) resetDraft();

    //Set plate in Vehicle A draft store
    if (vehicleAPlate.current) setVehicleAPlateStore(vehicleAPlate.current);

    setDraftId(draftId);
    setDraftStatus(response.step);
  };

  //On Confirm Plate Modal
  const onConfirmPlateModel = (data: InfoRequestDTO) => {
    if (data.targa) vehicleAPlate.current = data.targa;

    if (isLogged) {
      postDraftRequestLogged({
        proclie: proclie,
      });
      return;
    }

    postDraftRequestNotLogged(data);
  };

  // Navigation logic based on draft status
  useEffect(() => {
    if (!draftStatus) return;

    switch (draftStatus) {
      case BozzaStepDTO.APERTA:
      case BozzaStepDTO.VEICOLO_A:
        setCurrentStep(FormikStep.VEHICLE_A);
        navigate(Paths.DataEntry1, { replace: true });
        break;
      case BozzaStepDTO.VEICOLO_B:
        setCurrentStep(FormikStep.VEHICLE_B);
        navigate(Paths.DataEntry2, { replace: true });
        break;
      case BozzaStepDTO.DATI_GENERALI:
        setCurrentStep(FormikStep.ACCIDENT_DATA);
        navigate(Paths.DataEntry3, { replace: true });
        break;
      case BozzaStepDTO.ATTESA_CONDUCENTE_A:
      case BozzaStepDTO.DA_FIRMARE:
        setCurrentStep(FormikStep.SIGNATURE_A);
        navigate(Paths.DataEntry4, { replace: true });
        break;
      default:
        navigate("*", { replace: true });
        break;
    }
  }, [draftStatus]);

  // ===========================================================================
  // Starting logic
  useEffect(() => {
    const init = async () => {
      if (!Cookies.get(COGNITO_ID_TOKEN_KEY)) {
        try {
          await requestToken();
        } catch (error) {
          console.error("Error during retrieving the token:", error);
        }
      }

      resetCurrentStep();
      resetDraft();

      //If not logged
      if (!isLogged) {
        resetDraftId();
        showModalPlate();
        return;
      }

      const vehicleAPlateValue = vehicleAPlate.current;

      //If logged without plate in URL
      if (isLogged && !vehicleAPlateValue) {
        showModalPlate();
        return;
      }

      //Set plate in Vehicle A draft store
      if (vehicleAPlateValue) setVehicleAPlateStore(vehicleAPlateValue);

      //If logged and has plate in URL
      postDraftRequestLogged({
        proclie: proclie,
      });
    };

    init();
  }, []);

  return { modalPlateVisibility, onConfirmPlateModel };
};
